import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Space, Typography, Alert, Radio, InputNumber, Row, Col, Divider, Grid } from 'antd';
import { BankOutlined, MobileOutlined, DollarOutlined } from '@ant-design/icons';
import { useSelfRegistration } from '../../hooks/useSelfRegistration';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { formatAmount } from '@/components/common/utils/money';
import { useGetTransactionStatus } from '@/hooks/invitations/invitations';
import { SelfRegistrationSessionDTO,SelfOnboardingConfigDTO, FspType } from '@/shared/api/model';


// Import existing payment components
import BankPaymentHandler from '../BankPaymentHandler';
import MobilePaymentHandler from '../MobilePaymentHandler';
import { PaymentStatusModal } from '../PaymentStatusModal';
import { BankProcessModal } from '../BankProcessModal';
import TransactionPricingModal from '../TransactionPricingModal';
import DirectDepositHandler from '../DirectDepositHandler';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface DepositStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
    config: SelfOnboardingConfigDTO;
}

const DepositStep: React.FC<DepositStepProps> = ({
    session,
    onComplete,
    onError,
    config
}) => {
    const [form] = Form.useForm();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [isSubmittingDirectDeposit, setIsSubmittingDirectDeposit] = useState(false);
    
    // Hooks
    const { mutateAsync: getTransactionStatus } = useGetTransactionStatus();
    
    // State for deposit processing
    const [paymentType, setPaymentType] = useState<FspType | null>(null);
    const [selectedBank, setSelectedBank] = useState<string>('');
    const [selectedMno, setSelectedMno] = useState<string>('');
    const [depositAmount, setDepositAmount] = useState<number>(0);
    const [mobilePhoneNumber, setMobilePhoneNumber] = useState<string>('');
    
    // Use the passed-in config for deposit limits
    const depositConfig = {
        minDepositAmount: config.minDepositAmount || 10000,
        maxDepositAmount: config.maxDepositAmount || 1000000,
        currency: 'TZS'
    };
    
    // Modal states (reusing from legacy system)
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [isBankProcessOpen, setIsBankProcessOpen] = useState(false);
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
    const [paymentReference, setPaymentReference] = useState<string>('');
    const [transactionRequestId, setTransactionRequestId] = useState<string>('');
    const [priceResult, setPriceResult] = useState<any>(null);
    const [isPolling, setIsPolling] = useState(false);

    // Use the self registration hook
    const {
        processDeposit,
        updateDepositInfo,
        isProcessingDeposit,
        isUpdatingDepositInfo
    } = useSelfRegistration();

    const getDefaultPaymentType = () => {
        if (config.enableDirectBankDeposit) {
            return FspType.DIRECT_DEPOSIT;
        }
        if (config.enableBankPushDeposit) {
            return FspType.BANK;
        }
        if (config.enableMobileMoneyDeposit) {
            return FspType.MNO;
        }
        return null;
    };

    // Initialize form with default values and react to session changes
    useEffect(() => {
        const minAmount = depositConfig.minDepositAmount;
        setDepositAmount(minAmount);
        
        // If direct deposit evidence is pending review, set payment type to DIRECT_DEPOSIT
        if (session.directDepositPendingReview) {
            console.log('🔄 Direct deposit pending review detected, setting payment type to DIRECT_DEPOSIT');
            form.setFieldsValue({
                amount: session.directDepositAmount || minAmount,
                paymentType: FspType.DIRECT_DEPOSIT
            });
            setPaymentType(FspType.DIRECT_DEPOSIT);
        } else {
            const defaultPaymentType = getDefaultPaymentType();
            form.setFieldsValue({
                amount: minAmount,
                paymentType: defaultPaymentType
            });
            setPaymentType(defaultPaymentType);
        }
    }, [config.enableBankPushDeposit, config.enableDirectBankDeposit, config.enableMobileMoneyDeposit, form, depositConfig.minDepositAmount, session.directDepositPendingReview, session.directDepositAmount]);

    // Add effect to log session changes for debugging
    useEffect(() => {
        console.log('🔄 DepositStep session updated:', {
            directDepositPendingReview: session.directDepositPendingReview,
            directDepositAmount: session.directDepositAmount,
            directDepositPaymentDate: session.directDepositPaymentDate,
            status: session.status,
            currentStep: session.currentStep
        });
    }, [session]);

    const handlePaymentTypeChange = (e: any) => {
        const type = e.target.value as FspType;
        setPaymentType(type);
        form.setFieldsValue({ paymentType: type });
    };

    const handleAmountChange = (value: number | null) => {
        if (value) {
            setDepositAmount(value);
        }
    };

    const handleBankSelect = (bankCode: string) => {
        setSelectedBank(bankCode);
        form.setFieldsValue({ bankCode });
    };

    const handleMnoSelect = (mnoCode: string) => {
        setSelectedMno(mnoCode);
        form.setFieldsValue({ mnoCode });
    };

    const handlePhoneNumberChange = (phone: string) => {
        setMobilePhoneNumber(phone);
    }

    const validateAmount = (_: any, value: number) => {
        if (!value) {
            return Promise.reject(new Error('Please enter deposit amount'));
        }
        if (value < depositConfig.minDepositAmount) {
            return Promise.reject(new Error(`Minimum deposit amount is ${formatAmount(depositConfig.minDepositAmount)} TZS`));
        }
        if (value > depositConfig.maxDepositAmount) {
            return Promise.reject(new Error(`Maximum deposit amount is ${formatAmount(depositConfig.maxDepositAmount)} TZS`));
        }
        return Promise.resolve();
    };

    const handleDirectDepositSubmit = async (values: any) => {
        console.log('Direct Deposit Submission:', values);
        setIsSubmittingDirectDeposit(true);
        // TODO: Implement backend submission logic here
        // For now, simulate a delay and then call onComplete
        setTimeout(() => {
            setIsSubmittingDirectDeposit(false);
            showSuccessNotification('Success', 'Your deposit information has been submitted for review.');
            onComplete(session); // Or a modified session object
        }, 2000);
    };

    const handleDepositSubmit = async (values: any) => {
        try {
            const depositRequest = {
                amount: values.amount,
                fspType: values.paymentType,
                fspCode: values.paymentType === FspType.BANK ? selectedBank : selectedMno,
                phoneNumber: values.paymentType === FspType.MNO ? mobilePhoneNumber : session.phoneNumber,
                description: `Initial deposit for ${session.fullName}`
            };

            const result = await processDeposit(session.sessionToken!, depositRequest);

            if (result) {
                showSuccessNotification('Success', 'Deposit initiated successfully!');
                
                const depositData = result;
                if (values.paymentType === FspType.BANK) {
                    // For bank deposits, show process modal with instructions
                    setPaymentReference(depositData.paymentReference || '');
                    setIsBankProcessOpen(true);
                } else {
                    // For mobile money, start status polling
                    setTransactionRequestId(depositData.paymentReference || '');
                    setIsPaymentModalVisible(true);
                    startStatusPolling(depositData.paymentReference || '');
                    // Don't call onComplete here - wait for polling to confirm payment
                }
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to process deposit';
            showErrorNotification('Error', errorMessage);
            onError(errorMessage);
        }
    };

    // Cleanup polling interval on component unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, []);

    const startStatusPolling = (requestId: string) => {
        // Clear any existing polling interval
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
        
        setIsPolling(true);
        let pollCount = 0;
        const maxPolls = 30; // 5 minutes maximum (with 10-second intervals) - matching legacy
        
        pollingIntervalRef.current = setInterval(async () => {
            try {
                pollCount++;
                console.log(`🔄 Polling payment status (${pollCount}/${maxPolls}) for request: ${requestId}`);
                
                // Use the proper transaction status API hook (matching legacy implementation)
                const result = await getTransactionStatus(requestId);
                
                // Check for CONFIRMED status (matching legacy ScaleTransactionStatus.CONFIRMED)
                if (result.trxStatus === 'CONFIRMED') {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        setIsPolling(false);
                        setIsPaymentModalVisible(false);
                        showSuccessNotification('Payment confirmed successfully!');
                        onComplete({
                            ...session,
                            depositCompleted: true,
                            currentStep: 'REGISTRATION_COMPLETE'
                        });
                    } else if (result.trxStatus === 'FAILED') {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        setIsPolling(false);
                        setIsPaymentModalVisible(false);
                        onError('Payment failed. Please try again.');
                    } else if (pollCount >= maxPolls) {
                        // Timeout after max attempts
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        setIsPolling(false);
                        setIsPaymentModalVisible(false);
                        onError('Payment status check timed out. Please check your payment manually.');
                    }

                
            } catch (error) {
                console.error('Error polling payment status:', error);
                if (pollCount >= maxPolls) {
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }
                    setIsPolling(false);
                    setIsPaymentModalVisible(false);
                    onError('Payment status check failed. Please try again.');
                }
            }
        }, 10000); // Poll every 10 seconds (matching legacy)
    };


    const radioStyle = (isSelected: boolean) => ({
        width: '100%',
        height: isMobile ? '70px' : '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #d9d9d9',
        borderRadius: '8px',
        backgroundColor: isSelected ? '#1890ff' : '#fff',
        color: isSelected ? '#fff' : '#000',
        transition: 'all 0.3s ease'
    });

    return (
        <Card 
            className="w-full max-w-2xl mx-auto"
            size={isMobile ? 'small' : 'default'}
        >
            <div className="text-center mb-6">
                <DollarOutlined 
                    className="text-4xl text-blue-500 mb-4" 
                    style={{ fontSize: isMobile ? '36px' : '48px' }}
                />
                <Title level={isMobile ? 4 : 3}>Initial Deposit</Title>
                <Paragraph 
                    className="text-gray-600"
                    style={{ fontSize: isMobile ? '14px' : '16px' }}
                >
                    Complete your registration by making an initial deposit to activate your account.
                </Paragraph>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleDepositSubmit}
                className="space-y-4"
                size={isMobile ? 'middle' : 'large'}
            >
                {/* Deposit Amount */}
                <Form.Item
                    name="amount"
                    label="Deposit Amount (TZS)"
                    rules={[
                        { required: true, message: 'Please enter deposit amount' },
                        { validator: validateAmount }
                    ]}
                >
                    <InputNumber
                        size={isMobile ? 'middle' : 'large'}
                        style={{ 
                            width: '100%',
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px'
                        }}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) || 0 : 0}
                        onChange={handleAmountChange}
                        min={depositConfig.minDepositAmount}
                        max={depositConfig.maxDepositAmount}
                        placeholder={`Enter amount between ${depositConfig.minDepositAmount.toLocaleString()} - ${depositConfig.maxDepositAmount.toLocaleString()} TZS`}
                    />
                </Form.Item>

                <Alert
                    message={`Deposit Range: ${formatAmount(depositConfig.minDepositAmount)} - ${formatAmount(depositConfig.maxDepositAmount)} TZS`}
                    type="info"
                    showIcon
                    // @ts-ignore
                    size={isMobile ? 'small' : 'default'}
                    className="mb-4"
                />

                <Divider />

                {/* Payment Method Selection */}
                <Form.Item
                    name="paymentType"
                    label="Payment Method"
                    rules={[{ required: true, message: 'Please select payment method' }]}
                >
                    {session.directDepositPendingReview ? (
                        // Show only direct deposit option when pending review
                        <Alert
                            message="Payment Method Locked"
                            description="Direct deposit evidence is pending review. Other payment methods are temporarily disabled."
                            type="warning"
                            showIcon
                            // @ts-ignore
                            size={isMobile ? 'small' : 'default'}
                            className="mb-4"
                        />
                    ) : null}
                    
                    <Radio.Group 
                        onChange={handlePaymentTypeChange} 
                        size={isMobile ? 'middle' : 'large'} 
                        className="w-full"
                        disabled={session.directDepositPendingReview && paymentType !== "DIRECT_DEPOSIT"}
                    >
                        <Row gutter={isMobile ? [8, 8] : [16, 16]}>
                            {config.enableBankPushDeposit && (
                                <Col span={!config.enableMobileMoneyDeposit || !config.enableDirectBankDeposit ? 24 : 8}>
                                    <Radio.Button 
                                        value={FspType.BANK} 
                                        style={radioStyle(paymentType === FspType.BANK)}
                                        disabled={session.directDepositPendingReview}
                                    >
                                        <div className="text-center">
                                            <BankOutlined style={{ 
                                                fontSize: isMobile ? '20px' : '24px', 
                                                display: 'block', 
                                                marginBottom: '4px' 
                                            }} />
                                            <span style={{ 
                                                fontSize: isMobile ? '12px' : '14px', 
                                                fontWeight: '500' 
                                            }}>Bank Deposit</span>
                                        </div>
                                    </Radio.Button>
                                </Col>
                            )}
                            {config.enableMobileMoneyDeposit && (
                                <Col span={!config.enableBankPushDeposit || !config.enableDirectBankDeposit ? 24 : 8}>
                                    <Radio.Button 
                                        value={FspType.MNO} 
                                        style={radioStyle(paymentType === FspType.MNO)}
                                        disabled={session.directDepositPendingReview}
                                    >
                                        <div className="text-center">
                                            <MobileOutlined style={{ 
                                                fontSize: isMobile ? '20px' : '24px', 
                                                display: 'block', 
                                                marginBottom: '4px' 
                                            }} />
                                            <span style={{ 
                                                fontSize: isMobile ? '12px' : '14px', 
                                                fontWeight: '500' 
                                            }}>Mobile Money</span>
                                        </div>
                                    </Radio.Button>
                                </Col>
                            )}
                            {config.enableDirectBankDeposit && (
                                <Col span={!config.enableBankPushDeposit || !config.enableMobileMoneyDeposit ? 24 : 8}>
                                    <Radio.Button 
                                        value={FspType.DIRECT_DEPOSIT} 
                                        style={radioStyle(paymentType === FspType.DIRECT_DEPOSIT)}
                                    >
                                        <div className="text-center">
                                            <DollarOutlined style={{ 
                                                fontSize: isMobile ? '20px' : '24px', 
                                                display: 'block', 
                                                marginBottom: '4px' 
                                            }} />
                                            <span style={{ 
                                                fontSize: isMobile ? '12px' : '14px', 
                                                fontWeight: '500' 
                                            }}>Direct Transfer</span>
                                        </div>
                                    </Radio.Button>
                                </Col>
                            )}
                        </Row>
                    </Radio.Group>
                </Form.Item>

                {/* Bank Payment Handler */}
                {paymentType === FspType.BANK && (
                    <BankPaymentHandler
                        onBankSelect={handleBankSelect}
                        onAmountChange={(amount) => handleAmountChange(parseFloat(amount))}
                        config={depositConfig}
                        dark={false}
                    />
                )}

                {/* Mobile Payment Handler */}
                {paymentType === FspType.MNO && (
                    <MobilePaymentHandler
                        onFspChange={handleMnoSelect}
                        onAmountChange={(amount) => handleAmountChange(parseFloat(amount))}
                        onPhoneNumberChange={handlePhoneNumberChange}
                        config={depositConfig}
                        dark={false}
                    />
                )}

                {/* Direct Deposit Handler */}
                {paymentType === FspType.DIRECT_DEPOSIT && (
                    <div>
                        <Title level={isMobile ? 5 : 4}>Direct Bank Transfer</Title>
                        
                        {/* Check if direct deposit evidence is pending review */}
                        {session.directDepositPendingReview ? (
                            <div>
                                <Alert
                                    message="Direct Deposit Evidence Submitted"
                                    description={
                                        <div style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                            <p>Your direct deposit evidence has been submitted and is currently under review by our officers.</p>
                                            <p><strong>Submitted Details:</strong></p>
                                            <ul>
                                                <li>Amount: {session.directDepositAmount?.toLocaleString()} TZS</li>
                                                <li>Payment Date: {session.directDepositPaymentDate}</li>
                                                <li>Status: Pending Officer Review</li>
                                            </ul>
                                            <p>You will be notified once the review is complete. Please wait for approval before proceeding.</p>
                                        </div>
                                    }
                                    type="info"
                                    showIcon
                                    // @ts-ignore
                                    size={isMobile ? 'small' : 'default'}
                                    className="mb-4"
                                />
                            </div>
                        ) : (
                            <div>
                                <Paragraph style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                    Please deposit to the following account:
                                </Paragraph>
                                <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Bank:</Text> 
                                <Text style={{ fontSize: isMobile ? '14px' : '16px' }}>{config.directDepositBankName}</Text><br/>
                                <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Account Name:</Text> 
                                <Text style={{ fontSize: isMobile ? '14px' : '16px' }}>{config.directDepositAccountName}</Text><br/>
                                <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Account Number:</Text> 
                                <Text style={{ fontSize: isMobile ? '14px' : '16px' }}>{config.directDepositAccountNumber}</Text><br/>
                                <Divider/>
                                <Paragraph style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                    After making the deposit, please upload your proof of payment below.
                                </Paragraph>
                                <DirectDepositHandler 
                                    session={session} 
                                    onComplete={onComplete} 
                                    onError={onError} 
                                />
                            </div>
                        )}
                    </div>
                )}

                <Divider />

                {/* Submit Button */}
                <Form.Item>
                    <Space 
                        className="w-full justify-between"
                        direction={isMobile ? 'vertical' : 'horizontal'}
                        size={isMobile ? 'middle' : 'large'}
                    >
                        <Button 
                            size={isMobile ? 'middle' : 'large'} 
                            disabled
                            style={{ 
                                width: isMobile ? '100%' : 'auto',
                                height: isMobile ? '44px' : '48px'
                            }}
                        >
                            Back
                        </Button>
                        <Button
                            type="primary"
                            size={isMobile ? 'middle' : 'large'}
                            htmlType="submit"
                            loading={isProcessingDeposit}
                            disabled={
                                !paymentType ||
                                paymentType === FspType.DIRECT_DEPOSIT ||
                                depositAmount <= 0 ||
                                (paymentType === FspType.BANK && !selectedBank) ||
                                (paymentType === FspType.MNO && (!selectedMno || !mobilePhoneNumber))
                            }
                            style={{ 
                                width: isMobile ? '100%' : 'auto',
                                height: isMobile ? '44px' : '48px',
                                fontSize: isMobile ? '16px' : '18px'
                            }}
                        >
                            {paymentType === FspType.BANK ? 'Generate Payment Bill' : 'Process Payment'}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>

            {/* Payment Status Modal for Mobile Money */}
            <PaymentStatusModal
                open={isPaymentModalVisible}
                fspType={paymentType}
            />

            {/* Bank Process Modal */}
            <BankProcessModal
                open={isBankProcessOpen}
                onClose={() => setIsBankProcessOpen(false)}
                paymentReference={paymentReference}
            />

            {/* Transaction Pricing Modal */}
            <TransactionPricingModal
                open={isPricingModalOpen}
                onClose={() => setIsPricingModalOpen(false)}
                onConfirm={() => {/* handle price confirmation */}}
                priceResult={priceResult}
                dark={false}
            />
        </Card>
    );
};

export default DepositStep;
